import React, { useEffect, useRef, useState } from 'react';
import '../bundles.css';
import { deleteBundle, saveBundle } from '../../util/apiUtils';
import ReturnToOrder from '../ReturnToOrder';
import { useIsMounted } from '../../hooks/useIsMounted';
import { getBillingTypeDisplayName } from '../../util/gearOrderUtil';
import { getSupportedBillingMethodInputFields } from '../../BillingOptions';
import BillingMethodModal from './BillingMethodModal';
import Modal from '../../components/Modal';

/**
 * Displays a bundle summary and controls to customize and save changes.
 */
const BundleSummary = ({ bundle, productDetails, onRemove, onCustomize, updateBundle }) => {
	const [bundleName, setBundleName] = useState(bundle?.name || '');
	const [hasError, setHasError] = useState(false);
	const [saveInProgress, setSaveInProgress] = useState(false);
	const [saveStatus, setSaveStatus] = useState(null);
	const [billingOptions, setBillingOptions] = useState([{ name: process.env.REACT_APP_BILLING_SKIP }]);
	const [showBillingMethodModal, setShowBillingMethodModal] = useState(false);
	const [unsupportedBillingOptionSelected, setUnsupportedBillingOptionSelected] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const nameInputRef = useRef(null);
	const isMounted = useIsMounted();

	const hasAutoSelections = productDetails?.some((product) => product.autoSelect);

	useEffect(() => {
		if (bundleName) {
			setHasError(false);
		}
	}, [bundleName]);

	const numberOfProducts = productDetails?.length;
	useEffect(() => {
		setSaveStatus(null);
	}, [numberOfProducts]);

	// Create new billing methods each time productDetails changes
	const practitioner = bundle?.practitioner;
	const billingMethod = bundle?.billingMethod;
	useEffect(() => {
		if (!practitioner || !productDetails?.length) {
			return;
		}

		// Get billing methods of each product (1 array per product)
		let profileBillingOptions = productDetails
			.filter((profile) => profile.omsRequisition?.orderableItem)
			.map((profile) =>
				getSupportedBillingMethodInputFields({ practitioner, orderableItem: profile.omsRequisition.orderableItem })?.map(
					(method) => ({ name: method.name, description: method.description, subBillingOptions: method.subBillingOptions })
				)
			);

		// Get billing methods that are available for EVERY product
		let intersection = profileBillingOptions?.length
			? profileBillingOptions.reduce((a, b) =>
					a.filter((option) => b.map((option) => option.name).includes(option.name))
			  )
			: [];

		// Get distinct billing methods that are available for ANY product
		let union = profileBillingOptions
			.flatMap((option) => option)
			.filter((option, index, array) => array.findIndex((x) => x.name === option.name) === index);

		// Include the selected option if it isn't already in the list (was previously selected and then the supporting product was removed)
		if (
			billingMethod &&
			billingMethod !== process.env.REACT_APP_BILLING_SKIP &&
			!findBillingOption({ billingOptions: union, billingMethod })
		) {
			union.push({
				name: billingMethod,
				partialSupport: true
			});
		}

		const options = [
			...union.map((option) => ({
				...option,
				partialSupport: intersection.length > 0 && !intersection.find((x) => x.name === option.name)
			}))
		];

		// Don't include the 'skip' option for UK clients
		if (!practitioner?.ukclient) {
			options.unshift({name: process.env.REACT_APP_BILLING_SKIP});
		}

		setBillingOptions(options);
	}, [productDetails, practitioner, billingMethod]);

	/**
	 * Any time the selected billing method or the list of possible billing options changes check
	 * if the selected option is only partially supported (not supported by 1 or more products).
	 */
	useEffect(() => {
		setUnsupportedBillingOptionSelected(
			!!(billingMethod && findBillingOption({ billingOptions, billingMethod })?.partialSupport)
		);
	}, [billingOptions, billingMethod]);

	const handleNameChange = (event) => {
		setBundleName(event.target.value);
		setSaveStatus(null);
	};

	const onSubmit = (event) => {
		event?.preventDefault && event.preventDefault();
		setSaveStatus(null);

		if (!bundleName?.trim()) {
			setHasError(true);
			nameInputRef.current.focus();
		} else {
			setHasError(false);

			const updatedBundle = {
				...bundle,
				name: bundleName,
				// Convert the list of Strings back into a list of Numbers
				// Include the selected billing method for each product that supports it
				products: productDetails.map((product) => {
					return {
						omsOrderableItemId: product.omsOrderableItemId,
						panel: product.panel,
						components: product.components?.map(Number) || [],
						addons: product.addons?.map(Number) || [],
						billingMethod: isBillingMethodValidForProduct(
							bundle.billingMethod,
							product.omsRequisition,
							bundle.practitioner
						)
							? bundle.billingMethod
							: null
					};
				})
			};

			setSaveInProgress(true);
			saveBundle(updatedBundle)
				.then((savedBundle) => {
					setSaveStatus('SUCCESS');
				})
				.catch((err) => {
					setSaveStatus('ERROR');
				})
				.finally(() => {
					if (isMounted.current) {
						setSaveInProgress(false);
					}
				});
		}
	};

	const onDelete = () => {
		setSaveInProgress(true);
		deleteBundle(bundle.gearBundleId)
			.then(() => {
				setSaveStatus('DELETED');
			})
			.catch((err) => {
				setSaveStatus('ERROR');
			})
			.finally(() => {
				if (isMounted.current) {
					setSaveInProgress(false);
				}
			});
	};

	/**
	 * Tests if the given billing method is available as an option for the omsRequisition/practitioner combo.
	 */
	const isBillingMethodValidForProduct = (billingMethod, omsRequisition, practitioner) => {
		return (
			billingMethod === process.env.REACT_APP_BILLING_SKIP ||
			!!findBillingOption({
				billingOptions: getSupportedBillingMethodInputFields({practitioner, orderableItem: omsRequisition?.orderableItem}),
				billingMethod
			})
		);
	};

	/**
	 * Finds the billing option in the list that matches the given billing method name.
	 * Sub-option billing methods will match and return their parent option by checking if the 
	 * billingMethod starts with the parent option's name plus a colon.
	 */
	const findBillingOption = ({ billingOptions, billingMethod }) => {
		if (!billingMethod) {
			return null;
		}

		return billingOptions?.find(
			(option) =>
				option.name === billingMethod ||
				(option.subBillingOptions?.subOptions?.length && billingMethod.startsWith(option.name + ':'))
		);
	};

	let billingOptionName = getBillingTypeDisplayName(bundle?.billingMethod);
	if (!billingOptionName || billingOptionName === process.env.REACT_APP_BILLING_SKIP) {
		billingOptionName = 'Not Selected';
	}

	return (
		<div className="panel panel-primary">
			<div className="panel-heading">
				<h4 className="panel-title">Edit Profile Preferences</h4>
			</div>
			<div className="panel-body" style={{ padding: '0' }}>
				{saveInProgress && (
					<div className="loading-shield" style={{ position: 'absolute' }}>
						<div className="center-absolute">
							<span
								className="fa fa-spinner fa-spin fa-fw"
								style={{ fontSize: '5em' }}
								aria-hidden="true"
							></span>
						</div>
					</div>
				)}
				<div style={{ position: 'relative', margin: '20px' }} className={hasError ? 'has-error' : null}>
					<label
						htmlFor="bundle-name"
						className="control-label small"
						style={{
							margin: '0',
							position: 'absolute',
							zIndex: '2',
							top: '-0.75em',
							left: '5px',
							backgroundColor: '#fff',
							padding: '0 5px'
						}}
					>
						Custom Profile Name
					</label>
					<div>
						{/* Wrap the input in a form to get submit on [Enter] functionality */}
						<form style={{ margin: '0' }} onSubmit={onSubmit}>
							<input
								ref={nameInputRef}
								value={bundleName}
								onChange={handleNameChange}
								className="form-control"
								id="bundle-name"
								style={{ borderRadius: '8px' }}
								maxLength={128}
							/>
						</form>
						{hasError && <span className="help-block mb-0 mt-0">Please enter a custom profile name</span>}
					</div>
				</div>

				{hasAutoSelections && (
					<div style={{ margin: '0 15px', display: 'flex' }}>
						<div style={{ padding: '0 5px' }}>
							<small>
								<span className="fa fa-info-circle text-info" aria-hidden="true"></span>
							</small>
						</div>
						<small>
							For your convenience, the main panel has been selected for these tests. To further customize
							your test selections, please use the profile's Customize button below.
						</small>
					</div>
				)}

				{productDetails?.map((item, index) => (
					<div key={item.omsOrderableItemId} className="bundle-product-card">
						<div className="bundle-product">
							<ProductTitle product={item} />
						</div>
						{saveStatus !== 'DELETED' && (
							<div className="bundle-actions">
								<button
									className="text-default bundle-action-button"
									onClick={() => {
										setSaveStatus(null);
										onCustomize(item);
									}}
								>
									Customize
								</button>
								<button
									className="text-danger bundle-action-button"
									onClick={() => {
										setSaveStatus(null);
										onRemove(item.omsOrderableItemId);
									}}
								>
									Remove
								</button>
							</div>
						)}
					</div>
				))}

				<div style={{ padding: '10px' }}>
					<strong>Billing Method:</strong> {billingOptionName}
					<button
						className={`btn btn-link btn-unstyled fa ${
							unsupportedBillingOptionSelected ? 'fa-warning' : 'fa-edit'
						}`}
						title="Edit Billing Method"
						style={{
							minWidth: 'inherit',
							padding: '0 10px',
							...(unsupportedBillingOptionSelected && { color: '#8a6d3b' })
						}}
						onClick={() => setShowBillingMethodModal(true)}
					></button>
				</div>

				{saveStatus === 'ERROR' && (
					<p className="text-danger" style={{ margin: '0 10px' }}>
						<i className="fa fa-exclamation-circle"></i>An unexpected error has occurred. Please try again.
					</p>
				)}

				{saveStatus === 'SUCCESS' && (
					<p className="text-center">
						<i className="fa fa-check-circle text-success" aria-hidden="true"></i>
						<b>{bundle.name || bundleName || 'The custom profile'}</b> has been saved.
						<br />
						<ReturnToOrder redirect={true} />
					</p>
				)}

				{saveStatus !== 'SUCCESS' && saveStatus !== 'DELETED' && (
					<button
						disabled={productDetails.length === 0}
						title="Save"
						className="btn btn-primary rounded-pill btn-sm pull-right me-2"
						onClick={onSubmit}
					>
						Save
					</button>
				)}

				{saveStatus === 'DELETED' && (
					<p className="text-center">
						<i className="fa fa-check-circle text-success" aria-hidden="true"></i>
						<b>{bundle.name}</b> has been deleted.
						<br />
						<ReturnToOrder redirect={true} />
					</p>
				)}

				{saveStatus !== 'DELETED' && !!bundle.gearBundleId && (
					<button
						title="Delete"
						className="btn btn-danger rounded-pill btn-sm pull-left ms-2"
						onClick={() => setShowDeleteConfirmation(true)}
					>
						Delete
					</button>
				)}
			</div>
			{showBillingMethodModal && (
				<BillingMethodModal
					onModalHidden={() => setShowBillingMethodModal(false)}
					onSave={(billingMethodName) => {
						updateBundle((prevBundle) => ({
							...prevBundle,
							billingMethod: billingMethodName
						}));
					}}
					billingOptions={billingOptions}
					practitioner={bundle?.practitioner}
					selectedBillingMethod={bundle?.billingMethod}
				/>
			)}
			{showDeleteConfirmation && (
				<Modal
					id={`delete-bundle-confirmation-modal`}
					title="Confirm Deletion"
					confirmAction="Delete"
					cancelAction="Cancel"
					onSave={onDelete}
					modalHidden={() => setShowDeleteConfirmation(false)}
				>
					<div>
						Delete custom profile "<strong>{bundle.name}</strong>"?
					</div>
				</Modal>
			)}
		</div>
	);
};

/**
 * Format and display a product name.
 *
 * Splits a product name on a hyphen, treating the text before the hyphen as the product title and
 * the text after the hyphen as a subtitle.
 * If the raw product name ends in an asterisk the asterisk will be moved to the product title.
 *
 * This logic is skipped if the product name contains "RMALL". In that case the unaltered name will
 * be displayed as the title.
 */
const ProductTitle = ({ product }) => {
	if (!product?.name?.length) {
		return null;
	}

	let title = product.name;
	let subtitle;
	const hyphenIndex = product.name.indexOf('-');
	if (hyphenIndex > 0 && !product.name.toUpperCase().includes('RMALL')) {
		title = product.name.substring(0, hyphenIndex).trim();
		subtitle = product.name.substring(hyphenIndex + 1).trim();
		if (subtitle.endsWith('*')) {
			title = title + '*';
			subtitle = subtitle.substring(0, subtitle.length - 1).trim();
		}
	}

	return (
		<>
			<div className="bundle-product-title">
				{product.autoSelect && (
					<small>
						<span
							className="fa fa-info-circle text-info me-1"
							aria-hidden="true"
							title="The main panel has been automatically selected"
						></span>
					</small>
				)}
				{title}
			</div>
			{subtitle && <div className="bundle-product-subtitle">{subtitle}</div>}
		</>
	);
};

export default BundleSummary;
